<script lang="ts">
  import { NodeViewWrapper, NodeViewContent } from "svelte-tiptap";
  import IconButton from "app/components/+design-system/IconButton/IconButton.svelte";
  import { fly } from "svelte/transition";
  import { onMount } from "svelte";
  import type { NodeViewProps } from "@tiptap/core";
  import GutterMenu from "../../+shared/GutterMenu.svelte";
  import { Left, Right } from "$components/+shared/OVIcon";

  export let isEditable: boolean;
  export let deleteNode: NodeViewProps["deleteNode"];
  $$restProps; // consume unused tiptap props

  let tableContainer: HTMLDivElement;
  let observer: ResizeObserver;
  let scrollPosition = 0;
  let isScrollable = false;
  let atMinScroll = false;
  let atMaxScroll = false;

  onMount(() => {
    // initialise scroll values
    updateScrollStatus();

    // watch for changes in the table container width to toggle visibility of the scroll button
    observer = new ResizeObserver(updateScrollStatus);

    observer.observe(tableContainer);

    return () => observer?.disconnect();
  });

  function updateScrollStatus() {
    // check if the table container is scrollable
    // (if the total width (including overflow) is greater than the visible width)
    isScrollable = tableContainer?.scrollWidth > tableContainer?.clientWidth;

    // check if the scroll position is at the start of the container
    atMinScroll = tableContainer?.scrollLeft === 0;

    // check if the scroll position is at the end of the container, with a tolerance of 2px
    atMaxScroll =
      tableContainer?.scrollWidth - tableContainer?.scrollLeft - 2 <= tableContainer?.clientWidth;

    // get the current scroll position (distance from leftmost)
    scrollPosition = tableContainer?.scrollLeft;
  }
</script>

<NodeViewWrapper class="group relative my-2 px-2">
  {#if isEditable}
    <GutterMenu on:delete-block={deleteNode} />
  {/if}

  <div class="my-4">
    <!-- scrollable table container -->
    <div
      class="no-scrollbar table-light dark:table-dark relative overflow-x-auto text-base"
      bind:this={tableContainer}
      on:scroll={updateScrollStatus}
    >
      <!-- TABLE CONTENT -->
      <NodeViewContent />
    </div>

    <!-- scroll left button -->
    <div class="absolute left-3 top-1 z-[5]">
      <div
        class="flex h-full items-center"
        style={isScrollable && !atMinScroll ? `width: 28px` : `width: 0`}
      >
        {#if isScrollable && !atMinScroll}
          <div
            transition:fly={{ x: -5 }}
            class="rounded-[5px] bg-white/80 backdrop-blur-sm dark:bg-neutral-900/80"
          >
            <IconButton
              icon={Left}
              a11yLabel="Scroll left"
              variant="primary"
              on:click={() =>
                tableContainer?.scrollTo({
                  left: (scrollPosition -= 150),
                  behavior: "smooth",
                })}
            />
          </div>
        {/if}
      </div>
    </div>

    <!-- scroll right button -->
    <div class="absolute right-3 top-1 z-[5]">
      <div
        class="clip-right flex h-full items-center transition-[width]"
        style={isScrollable && !atMaxScroll ? `width: 28px` : `width: 0`}
      >
        {#if isScrollable && !atMaxScroll}
          <div
            transition:fly={{ x: 5 }}
            class="rounded-[5px] bg-white/80 backdrop-blur-sm dark:bg-neutral-900/80"
          >
            <IconButton
              icon={Right}
              a11yLabel="Scroll right"
              variant="primary"
              on:click={() =>
                tableContainer?.scrollTo({
                  left: (scrollPosition += 150),
                  behavior: "smooth",
                })}
            />
          </div>
        {/if}
      </div>
    </div>
  </div>
</NodeViewWrapper>
